import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import service from "../../../../services/menuConfigurationService";
import { getMenuOnUserRole } from "../../../../redux/feature/manufacturing_app/menuSlice";
import { fetchMasterList } from "../../../../redux/feature/manufacturing_app/commonMasterListSlice";
import Constants from "../../../../common/Constants";
import "./MenuConfiguration.scss";
import MenuIcon from "./MenuIcon";

export const isHomeMenu = (node) => node && (String(node.hrefLink || "").replace(/\/$/, "").toLowerCase() === "/home" ||
  ((!node.hrefLink || node.hrefLink === "#") && node.name?.trim().toLowerCase() === "home"));
function fixedMenuKeys(entries) {
  const keys = new Set();
  for (const { node, key } of entries) if (isHomeMenu(node)) {
    const parts = key.split(".");
    while (parts.length) { keys.add(parts.join(".")); parts.pop(); }
  }
  return keys;
}
const savedSelection = (entries, code) => new Set([...entries.filter(({ node }) => node.roleInfo?.includes(code)).map(({ key }) => key), ...fixedMenuKeys(entries)]);

export function menuEntries(menu, prefix = "") {
  return (menu || []).flatMap((node, index) => {
    if (node.activity === "disabled") return [];
    const key = prefix ? `${prefix}.${index}` : String(index);
    return [{ node, key }, ...menuEntries(node.children, key)];
  });
}
export function toggleMenu(selected, key, checked, entries) {
  const next = new Set(selected);
  for (const entry of entries) {
    if (entry.key === key || entry.key.startsWith(`${key}.`)) {
      if (checked) next.add(entry.key); else next.delete(entry.key);
    }
  }
  if (checked) {
    const parts = key.split(".");
    while (parts.length > 1) { parts.pop(); next.add(parts.join(".")); }
  }
  for (const fixed of fixedMenuKeys(entries)) next.add(fixed);
  return next;
}

export default function MenuConfiguration() {
  const dispatch = useDispatch();
  const [data, setData] = useState({ roles: [], menus: [] });
  const [roleId, setRoleId] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [edit, setEdit] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [roleSearch, setRoleSearch] = useState("");
  const [menuSearch, setMenuSearch] = useState("");
  const role = data.roles.find((item) => item._id === roleId);
  const document = data.menus.find((item) => item._id === documentId);
  const entries = menuEntries(document?.menu);
  const fixed = fixedMenuKeys(entries);
  const matchingRoles = data.roles.filter((item) => `${item.name} ${item.code}`.toLowerCase().includes(roleSearch.toLowerCase().trim()));
  const query = menuSearch.toLowerCase().trim();
  const matches = entries.filter(({ node }) => (node.name || "").toLowerCase().includes(query)).map(({ key }) => key);
  const visible = new Set(entries.filter(({ key }) => !query || matches.some((match) => key === match || key.startsWith(`${match}.`) || match.startsWith(`${key}.`))).map(({ key }) => key));

  function accept(next, preferredRole, preferredDocument) {
    setData(next);
    setRoleId(next.roles.some((r) => r._id === preferredRole) ? preferredRole : next.roles[0]?._id || "");
    setDocumentId(next.menus.some((m) => m._id === preferredDocument) ? preferredDocument : next.menus[0]?._id || "");
    setDirty(false);
  }
  useEffect(() => {
    const controller = new AbortController();
    service.load(controller.signal).then((next) => { if (!controller.signal.aborted) accept(next); })
      .catch((err) => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    setSelected(savedSelection(menuEntries(document?.menu), role?.code));
    setDirty(false);
  }, [document, role]);
  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(""), 4500);
    return () => clearTimeout(timer);
  }, [message]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function perform(action, success, preferred = roleId) {
    setBusy(true); setError(""); setMessage("");
    try {
      const changed = await action();
      // Clear the successful edit immediately, even if the refresh later fails.
      setEdit(null); setDeleting(null); setDirty(false); setMessage(success);
      const next = await service.load();
      accept(next, changed?._id || preferred, documentId);
      dispatch(getMenuOnUserRole());
      dispatch(fetchMasterList(Constants.MASTER.ROLE));
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  const reload = () => perform(() => Promise.resolve(), "");
  const save = () => perform(() => service.saveMenus(roleId, {
    documentId, revision: document.revision, selected: [...selected],
  }), `Menus saved for ${role.name}.`);
  const discard = () => {
    setSelected(savedSelection(entries, role.code));
    setDirty(false); setError("");
  };
  const renderMenus = (nodes, prefix = "") => (nodes || []).map((node, index) => {
    const key = prefix ? `${prefix}.${index}` : String(index);
    if (!visible.has(key)) return null;
    const enabledChildren = node.children?.filter((child) => child.activity !== "disabled") || [];
    const hasChildren = !!enabledChildren.length;
    return <div key={key} className={`${prefix ? "mc-child" : "mc-menu-group"} ${hasChildren ? "mc-has-children" : ""}`}>
      <div className={`mc-menu-row ${selected.has(key) ? "is-selected" : ""}`}>
        <Form.Check id={`menu-${key}`} type="checkbox" checked={selected.has(key)} disabled={fixed.has(key)} label={node.name || "Unnamed menu"}
          onChange={(event) => { setSelected(toggleMenu(selected, key, event.target.checked, entries)); setDirty(true); setMessage(""); }} />
        {hasChildren && <span className="mc-child-count">{enabledChildren.length}</span>}
        {fixed.has(key) && <span className="mc-fixed-badge">Always included</span>}
      </div>
      {hasChildren && <div className="mc-children">{renderMenus(node.children, key)}</div>}
    </div>;
  });

  return <main id="main" className="main dashboard menu-configuration">
    <div className="mc-page-heading">
      <div><span className="mc-eyebrow"><MenuIcon name="shield" /> Administration</span>
        <h2>Roles and menu configuration</h2><p>Manage roles and choose the menus your team can access.</p></div>
    </div>
    <div className="mc-topbar">
      <div className="mc-save-context"><span className={`mc-state-dot ${dirty ? "is-dirty" : ""}`} />
        <div><strong>{role ? role.name : "Menu permissions"}</strong><span>{dirty ? "Unsaved changes" : "Select a role to manage access"}</span></div>
      </div>
      <div className="mc-top-actions">
        {message && <span className="mc-success" role="status"><MenuIcon name="check" /> {message}</span>}
        {dirty && <Button variant="link" disabled={busy} onClick={discard}>Discard menu changes</Button>}
        <div className="mc-primary-actions">
          <Button variant="outline-secondary" disabled={busy || loading || dirty} onClick={reload}><MenuIcon name="reload" /> Reload</Button>
          <Button disabled={busy || loading || !dirty || !role || !document} onClick={save}><MenuIcon name="check" /> {busy ? "Saving…" : "Save menu permissions"}</Button>
        </div>
      </div>
    </div>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {loading ? <p role="status"><Spinner as="span" size="sm" animation="border" /> Loading roles and menus…</p> :
      <div className="mc-workspace">
        <section className="mc-role-panel" aria-label="Roles">
          <div className="mc-panel">
            <div className="mc-panel-heading"><h3>Roles <span className="mc-count">{data.roles.length}</span></h3>
              <Button size="sm" variant="outline-primary" disabled={busy || dirty} onClick={() => { setError(""); setEdit({ code: "", name: "" }); }}><MenuIcon name="plus" /> Add role</Button></div>
            <div className="mc-search"><MenuIcon name="search" /><Form.Control type="search" aria-label="Search roles" placeholder="Search roles…" value={roleSearch} onChange={(event) => setRoleSearch(event.target.value)} /></div>
            {!data.roles.length && <p>No roles found. Add a role to begin.</p>}
            {!!data.roles.length && !matchingRoles.length && <p className="mc-empty">No matching roles.</p>}
            <div className="mc-role-list">{matchingRoles.map((item) => <button type="button" key={item._id}
              className={`mc-role ${item._id === roleId ? "active" : ""}`}
              disabled={busy || dirty} aria-pressed={item._id === roleId} onClick={() => { setRoleId(item._id); setMessage(""); }}>
              <span className="mc-role-icon" aria-hidden="true"><MenuIcon name="person" /></span>
              <span className="mc-role-text"><strong>{item.name}</strong><small>{item.code}</small></span>
              {item._id === roleId && <MenuIcon name="chevron" />}
            </button>)}</div>
            {role && <div className="mc-role-actions"><Button variant="outline-secondary" disabled={busy || dirty} onClick={() => { setError(""); setEdit({ ...role }); }}><MenuIcon name="pencil" /> Edit role</Button>
              <Button variant="outline-danger" disabled={busy || dirty || role.code === "ADMIN"} onClick={() => { setError(""); setDeleting(role); }}><MenuIcon name="trash" /> Delete role</Button></div>}
          </div>
        </section>
        <section className="mc-permissions-panel" aria-label="Menu permissions"><div className="mc-panel">
          <div className="mc-panel-heading"><div><h3>{role ? `Menus for ${role.name}` : "Select a role"}</h3><p>Choose the pages available to this role.</p></div>
            <span className="mc-selected-count">{selected.size} of {entries.length} selected</span></div>
          <div className="mc-search"><MenuIcon name="search" /><Form.Control type="search" aria-label="Search menus" placeholder="Search menus…" value={menuSearch} onChange={(event) => setMenuSearch(event.target.value)} /></div>
          {data.menus.length > 1 && <Form.Group className="mb-3" controlId="menu-document"><Form.Label>Menu set</Form.Label>
            <Form.Select value={documentId} disabled={busy || dirty} onChange={(event) => setDocumentId(event.target.value)}>
              {data.menus.map((doc, index) => <option value={doc._id} key={doc._id}>Menu set {index + 1}: {doc.menu.map((node) => node.name).slice(0, 3).join(", ")}</option>)}
            </Form.Select></Form.Group>}
          {!document ? <p>No menu document is configured for your customer.</p> : !entries.length ? <p>This menu set has no entries.</p> : role && <>
            <p className="mc-hint"><MenuIcon name="info" /> Selecting a group includes its children. Uncheck any pages you don’t need.</p>
            <fieldset className="mc-menu-grid" disabled={busy}>{renderMenus(document.menu)}</fieldset>
            {!visible.size && <p className="mc-empty">No menus match your search.</p>}
            {dirty && <p className="mc-dirty-hint">Save or discard changes before selecting another role.</p>}
          </>}
        </div></section>
      </div>}
    <Modal show={!!edit} onHide={() => { if (!busy) setEdit(null); }} backdrop="static" centered>
      <Modal.Header closeButton={!busy}><Modal.Title>{edit?._id ? "Edit role" : "Add role"}</Modal.Title></Modal.Header>
      <Form onSubmit={(event) => { event.preventDefault(); if (busy) return;
        perform(() => edit._id ? service.updateRole(edit._id, { name: edit.name, revision: edit.revision }) : service.createRole({ code: edit.code, name: edit.name }), "Role saved.");
      }}><Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="role-code" className="mb-3"><Form.Label>Role code</Form.Label><Form.Control value={edit?.code || ""} required maxLength={40} pattern="[A-Za-z][A-Za-z0-9_-]*" disabled={busy || !!edit?._id}
          onChange={(event) => setEdit({ ...edit, code: event.target.value.toUpperCase() })} />
          {edit?._id && <Form.Text>The code stays fixed because users and menus reference it.</Form.Text>}</Form.Group>
        <Form.Group controlId="role-name"><Form.Label>Role name</Form.Label><Form.Control value={edit?.name || ""} required maxLength={100} disabled={busy}
          onChange={(event) => setEdit({ ...edit, name: event.target.value })} /></Form.Group>
      </Modal.Body><Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setEdit(null)}>Cancel</Button>
        <Button type="submit" disabled={busy || !edit?.name.trim() || !edit?.code.trim()}>Save role</Button></Modal.Footer></Form>
    </Modal>
    <Modal show={!!deleting} onHide={() => { if (!busy) setDeleting(null); }} backdrop="static" centered>
      <Modal.Header closeButton={!busy}><Modal.Title>Delete role</Modal.Title></Modal.Header>
      <Modal.Body>{error && <Alert variant="danger">{error}</Alert>}Delete <strong>{deleting?.name}</strong>? This removes its assignments from all menu sets. Roles assigned to users cannot be deleted.</Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setDeleting(null)}>Cancel</Button>
        <Button variant="danger" disabled={busy} onClick={() => perform(() => service.deleteRole(deleting._id, deleting.revision), "Role deleted.", "")}>Delete permanently</Button></Modal.Footer>
    </Modal>
  </main>;
}
